import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import i18next from 'i18next'
import { staffService } from '@/services/api'
import { queryKeys } from '@/config/queryKeys'
import type { GetStaffParams } from '@/services/api/staff/staff.static'

export const useStaffList = (params?: GetStaffParams) => {
  return useQuery({
    queryKey: queryKeys.staff.list({ ...params }),
    queryFn: () => staffService.getStaff(params),
  })
}

export const useStaffMember = (id: string) => {
  return useQuery({
    queryKey: queryKeys.staff.detail(id),
    queryFn: () => staffService.getStaffMember(id),
    enabled: !!id,
  })
}

export const useCreateStaff = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: staffService.createStaff,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.staff.lists() })
      toast.success(i18next.t('staff:staffCreated'))
    },
    onError: () => {
      toast.error(i18next.t('staff:errors.createFailed'))
    },
  })
}

export const useUpdateStaff = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Parameters<typeof staffService.updateStaff>[1]
    }) => staffService.updateStaff(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.staff.lists() })
      queryClient.invalidateQueries({ queryKey: queryKeys.staff.detail(id) })
      toast.success(i18next.t('staff:staffUpdated'))
    },
    onError: () => {
      toast.error(i18next.t('staff:errors.updateFailed'))
    },
  })
}

export const useUpdateStaffStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Parameters<typeof staffService.updateStaffStatus>[1]
    }) => staffService.updateStaffStatus(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.staff.lists() })
      queryClient.invalidateQueries({ queryKey: queryKeys.staff.detail(id) })
      toast.success(i18next.t('staff:staffStatusUpdated'))
    },
    onError: () => {
      toast.error(i18next.t('common:errors.saveFailed'))
    },
  })
}

export const useDeleteStaff = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => staffService.deleteStaff(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.staff.lists() })
      toast.success(i18next.t('staff:staffDeleted'))
    },
    onError: () => {
      toast.error(i18next.t('staff:errors.deleteFailed'))
    },
  })
}
